import { useEffect, useState } from "react";
import { t } from "i18next";
import { isDesktop, isTablet } from "react-device-detect";
import { galleryOrder } from "./constants";

export const isFloat = (value) => {
  return (
    typeof value === "number" &&
    !Number.isNaN(value) &&
    !Number.isInteger(value)
  );
};

export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};
export const isInStandaloneMode = () =>
  "standalone" in window.navigator && window.navigator.standalone;

export const getURLValuationId = () => {
  return window.location.pathname.split("/")[2]?.toString();
};

export const isValuationLandingRoute = (path) => {
  const reg = new RegExp("^/valuations/\\d+/?$");
  return reg.test(path);
};

export const isNavbarRoute = (path) => {
  const reg = new RegExp("^/valuations/\\d+/[a-z]+");
  return (
    reg.test(path) &&
    !isAuthRoute(path) &&
    (!path.includes("finances/comparables") || isDesktop || isTablet) &&
    (path.split("/").pop() !== "home" || isDesktop || isTablet) &&
    path.split("/").pop() !== "about-us"
  );
};

export const isLandingRoute = (path) => {
  return !path.includes("valuation");
};

export const isAuthRoute = (path) => {
  return ["sign-in", "number-verification", "facebook-callback"].some((k) =>
    path.includes(k)
  );
};

export const getQuarterFromDate = (date) => {
  const month = new Date(date).getMonth();
  const year = new Date(date).getFullYear();

  return `Q${month > 8 ? 4 : month > 5 ? 3 : month > 2 ? 2 : 1} ${year % 100}`;
};

export const calculateDivHeight = (screenWidth, divStyle, contentArray) => {
  const tempDiv = document.createElement("div");
  Object.assign(tempDiv.style, divStyle);
  tempDiv.style.width = `${screenWidth}px`;
  tempDiv.style.position = "absolute";
  tempDiv.style.visibility = "hidden";
  tempDiv.style.whiteSpace = "normal";
  tempDiv.style.width = "fit-content";
  tempDiv.style.textAlign = "center";

  contentArray.forEach((item) => {
    const lineContainer = document.createElement("div");
    lineContainer.style.display = "block";
    lineContainer.style.marginBottom = `${item.marginBottom || 0}px`;

    const fontSize = item.fontSize || 16; // Default font size
    const lineHeight = item.lineHeight || 24; // Default line height
    const fontWeight = item.fontWeight || 400;

    const lineSpan = document.createElement("span");
    lineSpan.style.fontSize = `${fontSize}px`;
    lineSpan.style.lineHeight = `${lineHeight}px`;
    lineSpan.style.fontWeight = `${fontWeight}`;

    lineSpan.textContent = item.value || "";

    lineContainer.appendChild(lineSpan);
    tempDiv.appendChild(lineContainer);
  });

  document.body.appendChild(tempDiv);

  const computedHeight = tempDiv.offsetHeight;

  document.body.removeChild(tempDiv);

  return computedHeight;
};

export const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    // Function to update screen size when the window is resized
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array to run the effect only once

  return screenSize;
};

export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const parseFile = (fileKey) => {
  const fullName = fileKey.split("/").pop();
  return {
    name: fullName.split(".")[0] ?? fullName,
    type: fullName.split(".")[1] ?? "txt"
  };
};

export const getBlobFromStream = (response, type) => {
  const uint8Array = new Uint8Array(response.data.length);
  for (let i = 0; i < response.data.length; i++) {
    uint8Array[i] = response.data.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: type });
};

export const convertBoolean = (value) => {
  if (typeof value === "boolean") {
    return value ? t("labels.yes") : t("labels.no");
  } else if (typeof value === "string" && (value === "0" || value === "1")) {
    return value === "1" ? t("labels.yes") : t("labels.no");
  } else if (typeof value === "number" && (value === 0 || value === 1)) {
    return value === 1 ? t("labels.yes") : t("labels.no");
  } else {
    return "";
  }
};

export const findTransKeyFromEsValue = (dict, value) => {
  const translationFile = require("../locales/es/translation.json");

  return Object.keys(translationFile[dict]).find(
    (key) => translationFile[dict][key] === value
  );
};

export const rearrangeKeysByOrder = (photos) => {
  let rearrangedDict = {};
  galleryOrder.forEach((key) => {
    if (photos.hasOwnProperty(key)) {
      rearrangedDict[key] = photos[key];
    }
  });
  return rearrangedDict;
};

export const getBgEfficiencyScore = (index) => {
  switch (index) {
    case "A":
      return "bg-[#4AA35B]";
    case "B":
      return "bg-[#6DB458]";
    case "C":
      return "bg-[#C5D553]";
    case "D":
      return "bg-[#F7EB60]";
    case "E":
      return "bg-[#F3BA44]";
    case "F":
      return "bg-[#E47739]";
    case "G":
      return "bg-[#DA3732]";
    default:
      return "bg-accumin-neutral-500";
  }
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizeFirstLetterParagrah = (string) => {
  if (!string) return string;
  return string
    .split(/(?<=[.])\s+|\n+/)
    .map(
      (sentence) =>
        sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase()
    )
    .join(" ");
};
